export const QUIZ_FLOW_TYPES = {
    initial: 'initial',
    renewal: 'renewal',
    symptoms: 'symptoms',
};

export type QuizFlow = keyof typeof QUIZ_FLOW_TYPES;

export const QUIZ_EVENT_TYPES = {
    continueClick: 'quiz_continue_click',
    backClick: 'quiz_back_click',
    forwardClick: 'quiz_forward_click',
    acceptClick: 'quiz_accept_click',
    scrollBottom: 'quiz_scroll_bottom',
};
export const QUIZ_QUESTION_TYPES = {
    consultChoice: 'consult_choice',
    dateOfBirth: 'date_of_birth',
    freeText: 'free_text',
    howItWorks: 'how_it_works',
    iconRating: 'icon_rating',
    leadForm: 'lead_form',
    leadFormSSO: 'lead_form_sso',
    loading: 'loading',
    loadingGraph: 'loading_graph',
    loadingImage: 'loading_image',
    multiChoice: 'multi_choice',
    number: 'number',
    numberRating: 'number_rating',
    onboarding: 'onboarding',
    password: 'password_form',
    phoneNumber: 'phone_number',
    photoUpload: 'file_upload',
    preferenceSelect: 'preference_select',
    prefilled: 'prefilled_question_summary',
    singleChoice: 'single_choice',
    suburb: 'suburb',
    weightProgress: 'weight_progress',
    firstLastName: 'first_last_name',
    passwordField: 'password_field',
};

// Determine long state by question type
export const QUIZ_LONG_CONTENT_QUESTION_TYPES = [QUIZ_QUESTION_TYPES.prefilled];
// Determine continue visibility state by question type
export const QUIZ_HIDE_CONTINUE_QUESTION_TYPES = [
    QUIZ_QUESTION_TYPES.singleChoice,
    QUIZ_QUESTION_TYPES.iconRating,
];
// Skip navigation for these question types as it's handled within field itself
export const QUIZ_SKIP_NAVIGATION_QUESTION_TYPES = [
    QUIZ_QUESTION_TYPES.numberRating,
    QUIZ_QUESTION_TYPES.photoUpload,
    QUIZ_QUESTION_TYPES.leadFormSSO,
];

export type QuizRecord = {
    redirectUrl?: string;
    currentQuestionId: string;
    medications: string[];
    plans: string[];
    questionList: QuestionRecord[];
    sectionList: SectionRecord[];
    quizBrand: string;
    quizCoBrand: string;
    quizName: string;
    quizToken?: string;
    quizType: string;
    quizVersion: string;
    quizSubVersion: string;
    quizResumeSource: string;
    iterationOfProgressBar: 'percentage' | 'fraction';
    error?: string[];
    id?: number;
    uuid?: string;
    accessToken?: string;
    refreshToken?: string;
    gateType?: string;
};
export type QuestionRecord = {
    questionText: string;
    questionType: string;
    frontendStamp?: string;
    questionId: string;
    groupName?: string;
    prefillNeeded?: boolean;
    questionExplanation?: string;
    prefillAnswers: AnswerRecord[];
    userAnswers: AnswerRecord[];
    parentQuestion?: string;
    sectionId: string;
    medication?: string;
    answerList: AnswerRecord[];
    additionalText?: string;
    additionalField1?: string;
    additionalField2?: string;
    additionalField3?: string;
    additionalField4?: string;
    largeImage?: boolean;
    userInfo?: {
        height?: number;
        weight?: number;
        bmi?: number;
        hairGoal?: string;
    };
    estimateTimeText?: string;
    legalText?: string;
    questionImage?: string;
};
export type QuestionList = Array<
    {
        answerList: AnswerList;
        prefillAnswers: AnswerList;
        userAnswers: AnswerList;
    } & QuestionRecord
>;
export type SectionRecord = {
    sectionId: string;
    sectionName: string;
};

export type PrefillData = {
    prefillQuestionIds: string[];
    prefillById: PrefillById;
};
export type PrefillById = {
    [key: string]: PrefillQuestion;
};
export type PrefillQuestion = {
    questionId: string;
    questionList: QuestionRecord[];
    sectionById: PrefillSectionById;
};
export type PrefillSectionById = {
    [key: string | number]: PrefillSection;
};
export type PrefillSection = {
    sectionId: string | number;
    questionList: QuestionRecord[];
};

export type AnswerRecord = {
    answerFrontendStamp?: string;
    frontendStamp?: string;
    answerId: string;
    answerText: string;
    deselectOthers?: boolean;
    image?: string;
    additionalField1?: string;
    additionalField2?: string;
    additionalField3?: string;
    redirectUrl?: string;
};

export type AnswerList = Array<AnswerRecord>;

export const QUIZ_BOOKING_STAGE_TYPES = {
    short: 'short',
    long: 'long',
    several_days: 'several_days',
    none: 'none',
} as const;

export enum QuizAttendStatus {
    CREATED = 'created',
    QUIZ_FINISHED = 'quiz_finished',
    REVIEWED = 'reviewed',
    RE_ENROLL = 're_enroll',
    CLOSED = 'closed',
    SCRIPT_PAID = 'script_paid',
    SCRIPT_CONSENT_FINISHED = 'script_consent_finished',
    VC_NEEDED = 'vc_needed',
    CLOSED_NOT_SUITABLE = 'closed_not_suitable',
    CONFIRMATION_NEEDED = 'confirmation_needed',
    NEW_CONSENT_NEEDED = 'new_consent_needed',
    NEW_PLAN_ACCEPTED = 'new_plan_accepted',
    RECOMMENDATION_EXPIRED = 'recommendation_expired',
    CLOSED_INELIGIBLE = 'closed_ineligible',
    AWAITING_REFERRAL_RESULTS = 'awaiting_referral_results',
    AWAITING_REFERRAL_RESULTS_APPOINTMENT = 'awaiting_referral_results_appointment',
}
